/**
 * checkoutIntent: remember which offer a player was checking out, so an
 * interruption can put them straight back.
 *
 * WHY: new players' first minutes are full of full-page redirects — the Didit
 * phone-gate round trip returns via /kyc/callback, and Supabase OAuth /
 * magic-link callbacks land on "/" (sometimes with ?error=bad_oauth_state /
 * otp_expired when a second flow raced the first). Every one of those landings
 * destroys whatever page the player was on. Telemetry (2026-08-13) showed
 * depositing players opening checkout, having the card form load fine, then
 * being dumped back on the lobby — repeatedly — and never reaching the Pay
 * button. The purchase intent was there; the app kept losing it.
 *
 * Mechanics: CheckoutPage saves {offerId, at} to sessionStorage whenever a
 * checkout is open. The lobby, on a mount shortly after a DOCUMENT load (not
 * SPA navigation), resumes a fresh intent exactly once per page load. The
 * Didit callback page reads it to send verified players back to their
 * checkout instead of the lobby. Explicit exits (back from checkout, purchase
 * complete, sign-out — authService.signOut clears sessionStorage) clear it.
 *
 * All storage access is wrapped: a storage failure degrades to "no intent",
 * never an error.
 */

const KEY = "cluck.checkoutIntent.v1";
const RESUMED_KEY = "cluck.checkoutIntent.v1.resumed";

// An intent older than this is stale — the player has moved on.
const MAX_AGE_MS = 10 * 60 * 1000;

// Resume only shortly after a document load. A LATER lobby mount is ordinary
// SPA navigation (the player chose to leave checkout) and must not be hijacked.
const RESUME_WINDOW_MS = 30 * 1000;

// One resume attempt per page load, ever — even if the lobby remounts.
let resumeAttemptedThisLoad = false;

function read() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.offerId || typeof parsed.at !== "number") return null;
    if (Date.now() - parsed.at > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Record that the player is (still) checking out this offer. */
export function saveCheckoutIntent(offerId) {
  if (!offerId) return;
  try {
    window.sessionStorage.setItem(
      KEY,
      JSON.stringify({ offerId, at: Date.now() }),
    );
  } catch {
    /* best-effort */
  }
}

/** The player deliberately left checkout / finished paying. */
export function clearCheckoutIntent() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* best-effort */
  }
}

/** Non-destructive read (Didit callback uses this to pick a destination). */
export function peekCheckoutIntent() {
  return read();
}

/**
 * One-shot resume check for the lobby: returns the offerId to resume when a
 * fresh intent exists AND this mount is shortly after a document load (an
 * interruption landing, not a chosen SPA navigation). Marks the resumption so
 * CheckoutPage can annotate its funnel event.
 * @returns {string|null}
 */
export function resumeCheckoutIntentOnce() {
  if (resumeAttemptedThisLoad) return null;
  resumeAttemptedThisLoad = true;
  try {
    if (performance.now() > RESUME_WINDOW_MS) return null;
  } catch {
    return null;
  }
  const intent = read();
  if (!intent) return null;
  try {
    window.sessionStorage.setItem(RESUMED_KEY, "1");
  } catch {
    /* annotation only */
  }
  return intent.offerId;
}

/**
 * Read-and-clear the "this checkout view is a resume" marker, so
 * checkout_viewed telemetry can distinguish resumed views from organic ones.
 * @returns {boolean}
 */
export function consumeResumedMarker() {
  try {
    const v = window.sessionStorage.getItem(RESUMED_KEY) === "1";
    window.sessionStorage.removeItem(RESUMED_KEY);
    return v;
  } catch {
    return false;
  }
}
